import React, { useState, useEffect, useCallback } from 'react';
import { generateSEOTags, applySEOTags } from '../utils/seoUtils';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  Modal,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Download,
  GitHub,
  Security,
  Speed,
  Memory,
  Devices,
  TrendingUp,
  Shield,
  CloudOff,
  Computer,
  Smartphone,
  Article,
  Apple,
  Computer as LinuxIcon,
  Star,
  Close,
  ZoomIn,
  Refresh,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

function detectOS() {
  const userAgent = window.navigator.userAgent;
  if (userAgent.indexOf('Win') !== -1) return 'windows';
  if (userAgent.indexOf('Mac') !== -1) return 'macos';
  if (userAgent.indexOf('Linux') !== -1) return 'linux';
  return 'linux';
}

function getInstallCommand(os) {
  switch (os) {
    case 'windows':
      return 'winget install HxHippy.PWGen-Rust';
    case 'macos':
      return 'brew install --cask pwgen-rust';
    case 'linux':
    default:
      return 'sudo snap install pwgen-rust';
  }
}

function getDownloadUrl(platform) {
  switch (platform) {
    case 'Windows':
      return '/releases/windows/installer/pwgen-gui-windows-x64.exe';
    case 'Linux':
      return 'https://snapcraft.io/pwgen-rust';
    case 'macOS':
    case 'Android':
    default:
      return null; // Coming soon platforms
  }
}

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [os, setOS] = useState('linux');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Password generator state
  const [password, setPassword] = useState('');
  const [length] = useState(16);
  const [includeUppercase] = useState(true);
  const [includeLowercase] = useState(true);
  const [includeNumbers] = useState(true);
  const [includeSymbols] = useState(true);
  const [copied, setCopied] = useState(false);

  // Generate password function
  const generatePassword = useCallback(() => {
    let charset = '';
    if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeNumbers) charset += '0123456789';
    if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    if (charset === '') {
      setPassword('Please select at least one character type');
      return;
    }
    
    let newPassword = '';
    for (let i = 0; i < length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setPassword(newPassword);
    setCopied(false);
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols]);

  useEffect(() => {
    setOS(detectOS());
    generatePassword(); // Generate initial password
  }, [generatePassword]);

  // Copy to clipboard function
  const copyPassword = async () => {
    if (password && password !== 'Please select at least one character type') {
      try {
        await navigator.clipboard.writeText(password);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy password');
      }
    }
  };

  // Regenerate password when settings change
  useEffect(() => {
    generatePassword();
  }, [generatePassword]);

  // SEO meta tags
  useEffect(() => {
    const seoTags = generateSEOTags({
      title: 'PWGen-Rust - Secure Password Manager Built in Rust',
      description: 'PWGen-Rust is a memory-safe, zero-knowledge password manager built in Rust. AES-256 encryption, cross-platform support, and open-source transparency.',
      keywords: ['password manager', 'Rust security', 'zero-knowledge', 'AES-256', 'open source', 'cross-platform', 'memory safe', 'encryption', 'cybersecurity', 'local first'],
      image: '/img/pwgen-social-card.png',
      url: window.location.href,
      type: 'website',
      author: 'Kief Studio',
      siteName: 'PWGen-Rust'
    });
    
    applySEOTags(seoTags);
  }, []);

  const features = [
    {
      icon: <Security />,
      title: 'Zero-Knowledge Architecture',
      description: 'Your master password and vault data never leave your device. Complete local control with no cloud dependencies.',
      highlight: true,
    },
    {
      icon: <Shield />,
      title: 'Military-Grade Encryption',
      description: 'AES-256-GCM encryption with PBKDF2 key derivation. Industry-standard security protocols.',
      highlight: true,
    },
    {
      icon: <Memory />,
      title: 'Memory Safe Rust',
      description: 'Built with Rust for memory safety, preventing buffer overflows and security vulnerabilities.',
      highlight: false,
    },
    {
      icon: <Speed />,
      title: 'High Performance',
      description: 'Optimized algorithms ensure instant password generation and vault operations.',
      highlight: false,
    },
    {
      icon: <CloudOff />,
      title: 'Offline First',
      description: 'Works completely offline. No internet connection required for core functionality.',
      highlight: false,
    },
    {
      icon: <Devices />,
      title: 'Cross-Platform',
      description: 'Available on Windows, macOS, Linux, and mobile platforms with consistent experience.',
      highlight: false,
    },
  ];


  const stats = [
    { value: 'v1.2.0', label: 'Latest Version', icon: <Star />, description: 'Open source' },
    { value: 'AES-256', label: 'Encryption', icon: <Security />, description: 'Military grade' },
    { value: 'Rust', label: 'Built With', icon: <Memory />, description: 'Memory safe' },
    { value: 'Apache 2.0', label: 'License', icon: <Article />, description: 'Core features free' },
  ];

  const platformData = [
    { name: 'Windows', icon: <Computer />, status: 'Available', installer: 'GUI/CLI .exe', version: 'v1.2.0' },
    { name: 'macOS', icon: <Apple />, status: 'Coming Soon', installer: '.dmg Package', version: 'Q1 2026' },
    { name: 'Linux', icon: <LinuxIcon />, status: 'Available', installer: 'Binary/Snap', version: 'v1.2.0' },
    { name: 'Android', icon: <Smartphone />, status: 'Coming Soon', installer: 'Play Store', version: 'Q1 2026' },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'flex-start',
          overflow: 'hidden',
          pt: { xs: '15vh', sm: '12vh', md: '10vh' },
        }}
      >
        {/* Background Video */}
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: { xs: 0.3, md: 0.4 },
            zIndex: 0,
          }}
        >
          <source src="/assets/videos/Crabs-Shoreline.mp4" type="video/mp4" />
        </Box>

        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(17, 24, 39, 0.65), rgba(31, 41, 55, 0.60))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.75), rgba(248, 250, 252, 0.70))',
            zIndex: 1,
          }}
        />

        <Container 
          maxWidth="xl" 
          sx={{ 
            position: 'relative', 
            zIndex: 2,
            px: { xs: 3, md: 4, lg: 6 },
            py: { xs: 1, md: 2 }
          }}
        >
          <Grid container spacing={{ xs: 6, lg: 8 }} alignItems="center" sx={{ minHeight: { md: '60vh' } }}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Stack spacing={{ xs: 3, md: 4 }} sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
                {/* Logo at Top */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', lg: 'flex-start' } }}>
                  <Box
                    component="img"
                    src={theme.palette.mode === 'dark' 
                      ? "/assets/images/PWGenLogo-Dark-Wide.png" 
                      : "/assets/images/PWGenLogo-Wide.png"
                    }
                    alt="PWGen-Rust - Zero-Knowledge Password Manager"
                    sx={{
                      height: { xs: 50, sm: 60, md: 80, lg: 100 },
                      width: 'auto',
                      dropShadow: theme.palette.mode === 'dark' 
                        ? 'drop-shadow(0 20px 40px rgba(255, 255, 255, 0.1))'
                        : 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.15))',
                      mb: 2,
                    }}
                  />
                  <Chip
                    icon={<Star />}
                    label="🚀 Version 1.2 Now Available"
                    variant="filled"
                    component="a"
                    href="https://github.com/KiefStudioMA/PWGen-Rust/releases/latest"
                    target="_blank"
                    clickable
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      px: 2,
                      py: 0.5,
                      textDecoration: 'none',
                      bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                      color: theme.palette.accent?.contrastText || 'white',
                      '&:hover': {
                        bgcolor: theme.palette.accent?.dark || theme.palette.primary.dark,
                        transform: 'translateY(-1px)',
                        boxShadow: 2,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.05,
                      mb: 2,
                      fontSize: { 
                        xs: '2rem', 
                        sm: '2.5rem', 
                        md: '3rem', 
                        lg: '3.5rem',
                        xl: '4rem'
                      },
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Enterprise-Grade
                    <br />
                    <Typography
                      component="span"
                      variant="inherit"
                      sx={{
                        background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 800,
                      }}
                    >
                      Password Security
                    </Typography>
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 400,
                      lineHeight: 1.5,
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.3rem' },
                      maxWidth: { xs: '100%', lg: '95%', xl: '90%' },
                      mx: { xs: 'auto', lg: 0 },
                    }}
                  >
                    Zero-knowledge password manager built with Rust. Military-grade encryption, 
                    complete local control, and enterprise-ready security features for modern teams.
                  </Typography>
                </Box>

                <Stack 
                  direction="row" 
                  spacing={{ xs: 1, md: 2 }} 
                  flexWrap="wrap" 
                  useFlexGap
                  sx={{ 
                    gap: { xs: 1, md: 2 },
                    justifyContent: { xs: 'center', lg: 'flex-start' },
                  }}
                >
                  {[
                    { icon: <Security />, label: 'Zero-Knowledge' },
                    { icon: <Memory />, label: 'Memory Safe' },
                    { icon: <Shield />, label: 'AES-256-GCM' },
                    { icon: <CloudOff />, label: 'Offline First' },
                  ].map((item, index) => (
                    <Chip
                      key={index}
                      icon={item.icon}
                      label={item.label}
                      variant={index < 2 ? 'filled' : 'outlined'}
                      size="medium"
                      sx={{ 
                        fontWeight: 500,
                        cursor: 'default',
                        backgroundColor: index < 2 ? theme.palette.accent?.main || '#00695c' : 'transparent',
                        color: index < 2 ? 'white' : theme.palette.accent?.main || '#00695c',
                        borderColor: index >= 2 ? theme.palette.accent?.main || '#00695c' : 'transparent',
                        '& .MuiChip-icon': {
                          color: index < 2 ? 'white' : theme.palette.accent?.main || '#00695c',
                        },
                        '&:hover': {
                          backgroundColor: index < 2 
                            ? theme.palette.accent?.dark || '#004d40'
                            : alpha(theme.palette.accent?.main || '#00695c', 0.08),
                        },
                        '&:focus': {
                          backgroundColor: index < 2 
                            ? theme.palette.accent?.main || '#00695c'
                            : 'transparent',
                        },
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                    />
                  ))}
                </Stack>

                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={{ xs: 2, md: 3 }}
                  sx={{ 
                    pt: 2,
                    justifyContent: { xs: 'center', lg: 'flex-start' },
                    alignItems: { xs: 'center', lg: 'flex-start' },
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Download />}
                    component={RouterLink}
                    to="/download"
                    sx={{
                      py: 1.8,
                      px: 5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      minWidth: { xs: '100%', sm: 220 },
                      whiteSpace: 'nowrap',
                      boxShadow: 3,
                      bgcolor: 'success.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'success.dark',
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Download PWGen-Rust
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<GitHub />}
                    href="https://github.com/KiefStudioMA/PWGen-Rust"
                    target="_blank"
                    sx={{
                      py: 1.8,
                      px: 5,
                      fontSize: '1.1rem',
                      borderRadius: 2,
                      borderWidth: 2,
                      minWidth: { xs: '100%', sm: 180 },
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    View Source
                  </Button>
                </Stack>

              </Stack>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Quick Install Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  background: alpha(theme.palette.primary.main, 0.04),
                  textAlign: { xs: 'center', lg: 'left' },
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                  Quick Install ({os}):
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    wordBreak: 'break-all',
                    lineHeight: 1.4,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    p: 1.5,
                    borderRadius: 1,
                  }}
                >
                  {getInstallCommand(os)}
                </Typography>
              </Paper>
              
              {/* Quick Password Generator */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  border: '2px solid',
                  borderColor: theme.palette.accent?.main || 'primary.main',
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.background.paper, 0.9)
                    : alpha('#ffffff', 0.9),
                  backdropFilter: 'blur(20px)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, ${theme.palette.accent?.main || theme.palette.primary.main}, ${theme.palette.primary.main})`,
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                  🔐 Quick Password Generator
                </Typography>
                
                {/* Generated Password Display */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    value={password}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Copy Password">
                            <IconButton onClick={copyPassword} sx={{ color: theme.palette.accent?.main || '#00695c' }}>
                              {copied ? <Check /> : <ContentCopy />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate New Password">
                            <IconButton onClick={generatePassword} sx={{ color: theme.palette.accent?.main || '#00695c' }}>
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ),
                      sx: {
                        fontFamily: 'monospace',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 600,
                        color: theme.palette.accent?.main || 'primary.main',
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? alpha(theme.palette.background.paper, 0.95)
                          : alpha('#ffffff', 0.95),
                        backdropFilter: 'blur(10px)',
                        '&:hover fieldset': {
                          borderColor: theme.palette.accent?.main || theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={generatePassword}
                    startIcon={<Refresh />}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                      color: theme.palette.accent?.contrastText || 'white',
                      '&:hover': {
                        bgcolor: theme.palette.accent?.dark || theme.palette.primary.dark,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Generate New
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    component={RouterLink}
                    to="/tools"
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    More Tools
                  </Button>
                </Stack>

                {copied && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 2,
                      color: 'success.main',
                      fontWeight: 600,
                    }}
                  >
                    Password copied to clipboard!
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 2, md: 4 }} sx={{ width: '100%' }}>
          {stats.map((stat, index) => (
            <Grid size={{ xs: 6, sm: 3 }} key={index} sx={{ display: 'flex' }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 4 },
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)',
                    boxShadow: 2,
                  },
                }}
              >
                <Box sx={{ color: 'primary.main', mb: { xs: 1, md: 2 } }}>
                  {React.cloneElement(stat.icon, { sx: { fontSize: { xs: 28, md: 36 } } })}
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 1,
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                  {stat.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  {stat.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Box id="features" sx={{ 
        py: { xs: 6, md: 8 }, 
        background: alpha(theme.palette.background.default, 0.5)
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography 
              variant="h2" 
              component="h2" 
              sx={{ 
                fontWeight: 700, 
                mb: 3,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Why Choose PWGen-Rust?
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '100%', 
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              Built from the ground up with security, performance, and user privacy as core principles.
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: feature.highlight ? (theme.palette.accent?.main || 'primary.main') : 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.accent?.main || 'primary.main',
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    },
                    ...(feature.highlight && {
                      background: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.05),
                    }),
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2, color: feature.highlight ? (theme.palette.accent?.main || 'primary.main') : 'primary.main' }}>
                      {React.cloneElement(feature.icon, { sx: { fontSize: 48 } })}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, flexGrow: 1 }}>
                      {feature.description}
                    </Typography>
                    {feature.highlight && (
                      <Chip
                        label="Core Feature"
                        size="small"
                        sx={{ 
                          mt: 2, 
                          alignSelf: 'flex-start',
                          bgcolor: theme.palette.accent?.main || theme.palette.primary.main,
                          color: theme.palette.accent?.contrastText || 'white',
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Platform Support */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Cross-Platform Support
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Available on all major platforms with consistent security and performance.
          </Typography>
        </Box>

        <TableContainer 
          component={Paper} 
          elevation={0} 
          sx={{ 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Installation</TableCell>
                <TableCell sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {platformData.map((platform, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ color: 'primary.main' }}>
                        {platform.icon}
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {platform.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={platform.status}
                      color={platform.status === 'Available' ? 'success' : 'warning'}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary">
                      {platform.installer}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary">
                      {platform.version}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant={platform.status === 'Available' ? 'contained' : 'outlined'}
                      disabled={platform.status !== 'Available'}
                      startIcon={<Download />}
                      href={getDownloadUrl(platform.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {isMobile ? 'Get' : 'Download'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* Screenshots Section */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box id="screenshots" sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            See PWGen-Rust in Action
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 6 }}>
            Explore the intuitive interface and powerful features of PWGen-Rust.
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ mb: { xs: 4, md: 0 } }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
                Password Generator
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                Generate secure, customizable passwords with our advanced generator. Configure length, 
                character sets, and complexity requirements to meet any security policy.
              </Typography>
              <Box
                onClick={() => setLightboxOpen(true)}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Box
                  component="img"
                  src="/assets/images/screenshot-generator.png"
                  alt="PWGen-Rust Password Generator"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.1) 0%, rgba(66, 165, 245, 0.05) 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                >
                  <ZoomIn sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
                Main Interface
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                Clean, intuitive interface for managing your passwords and secrets. Access your vault, 
                organize credentials, and manage security settings with ease.
              </Typography>
              <Box
                onClick={() => setLightboxOpen(true)}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <Box
                  component="img"
                  src="/assets/images/screenshot-main.png"
                  alt="PWGen-Rust Main Interface"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.1) 0%, rgba(66, 165, 245, 0.05) 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                >
                  <ZoomIn sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Security Credentials */}
      <Box id="security" sx={{ 
        py: { xs: 6, md: 8 }, 
        background: alpha(theme.palette.background.default, 0.5)
      }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              border: '2px solid',
              borderColor: theme.palette.accent?.main || 'primary.main',
              borderRadius: 3,
              background: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.05),
            }}
          >
            <Stack spacing={4} alignItems="center">
              <Box>
                <Chip
                  icon={<Shield />}
                  label="Developer: CCEH Certified"
                  variant="filled"
                  sx={{ 
                    mb: 3,
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 3,
                    px: 2,
                    backgroundColor: theme.palette.accent?.main || '#00695c',
                    color: 'white',
                    '& .MuiChip-icon': {
                      color: 'white',
                    },
                  }}
                />
                <Typography 
                  variant="h2" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 3,
                    fontSize: { xs: '2rem', md: '3rem' }
                  }}
                >
                  Built by Security Professionals
                </Typography>
                <Typography 
                  variant="h6" 
                  color="text.secondary"
                  sx={{ 
                    maxWidth: 800, 
                    mx: 'auto',
                    lineHeight: 1.6 
                  }}
                >
                  PWGen-Rust is developed by security professionals with industry certifications including 
                  CCEH (Cisco Certified Ethical Hacker). Built with defense-in-depth principles, rigorous 
                  threat modeling, and a commitment to protecting your most sensitive data with industry-leading practices.
                </Typography>
              </Box>
              
              <Grid container spacing={3} sx={{ maxWidth: 800 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Stack spacing={2} alignItems="center">
                    <Security sx={{ fontSize: 48, color: theme.palette.accent?.main || 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Defense in Depth
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      Multiple layers of security to protect your data
                    </Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Stack spacing={2} alignItems="center">
                    <Shield sx={{ fontSize: 48, color: theme.palette.accent?.main || 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Certified Standards
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      Built following industry security best practices
                    </Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Stack spacing={2} alignItems="center">
                    <TrendingUp sx={{ fontSize: 48, color: theme.palette.accent?.main || 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Always Improving
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      Regular updates based on latest security research
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 3,
            background: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Ready to Secure Your Digital Life?
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              mb: 4, 
              maxWidth: '100%', 
              textAlign: 'center',
              fontSize: { xs: '1rem', md: '1.25rem' }
            }}
          >
            Download PWGen-Rust today and experience enterprise-grade password security with complete local control.
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 2, md: 3 }} 
            justifyContent="center"
            sx={{ maxWidth: { xs: '100%', sm: 'none' }, mx: 'auto' }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Download />}
              component={RouterLink}
              to="/download"
              sx={{ 
                py: 1.5, 
                px: 4, 
                fontSize: '1.1rem',
                minWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              Download Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/docs"
              startIcon={<Article />}
              sx={{ 
                py: 1.5, 
                px: 4, 
                fontSize: '1.1rem',
                minWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              View Documentation
            </Button>
          </Stack>
        </Paper>
      </Container>

      {/* Lightbox Modal */}
      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>
          <Box
            component="img"
            src="/assets/images/screenshot-main.png"
            alt="PWGen-Rust Main Interface - Expanded View"
            onClick={() => setLightboxOpen(false)}
            sx={{
              width: '100%',
              height: '100%',
              maxWidth: '90vw',
              maxHeight: 'calc(90vh - 80px)',
              objectFit: 'contain',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
              borderRadius: 0,
              cursor: 'pointer',
            }}
          />
          <Button
            variant="contained"
            size="large"
            startIcon={<Download />}
            component={RouterLink}
            to="/download"
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: 'success.main',
              color: 'white',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'success.dark',
                boxShadow: 6,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Download PWGen-Rust
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default HomePage;